import React, { useEffect, useState, useCallback } from 'react';
import { axiosAuth } from '../contexts/AuthContext';
import { Coins, DollarSign, TrendingUp, Loader2, RefreshCw } from 'lucide-react';

const CreditsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/api/admin/credits/stats');
      setStats(data);
    } catch (err) {
      alert('Failed to load credits stats: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div data-testid="credits-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-['Bebas_Neue']">Credits</h2>
          <p className="text-sm text-[#A3A3A3]">
            Track streaming-credit revenue and per-user balances.
          </p>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#D4AF37] text-sm uppercase tracking-wider transition-colors"
          data-testid="credits-refresh-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#1A1A1A] border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Coins size={18} className="text-[#D4AF37]" />
            <p className="text-xs text-[#737373] uppercase tracking-wider">Credits Sold</p>
          </div>
          <p className="font-['Bebas_Neue'] text-4xl" data-testid="stat-credits-sold">
            {stats?.total_credits_sold ?? '–'}
          </p>
        </div>
        <div className="bg-[#1A1A1A] border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={18} className="text-[#D4AF37]" />
            <p className="text-xs text-[#737373] uppercase tracking-wider">Revenue</p>
          </div>
          <p className="font-['Bebas_Neue'] text-4xl text-[#D4AF37]" data-testid="stat-credits-revenue">
            ${stats?.total_revenue_usd?.toFixed(2) ?? '0.00'}
          </p>
        </div>
        <div className="bg-[#1A1A1A] border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={18} className="text-[#D4AF37]" />
            <p className="text-xs text-[#737373] uppercase tracking-wider">Pack Purchases</p>
          </div>
          <p className="font-['Bebas_Neue'] text-4xl" data-testid="stat-credits-purchases">{stats?.total_purchases ?? '–'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top balances */}
        <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
          <div className="px-4 py-3 bg-[#0D0D0D] border-b border-white/5">
            <h3 className="text-sm uppercase tracking-wider text-[#A3A3A3]">User Balances (top 50)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0D0D0D] text-[#737373] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-right">Credits</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.top_balances || []).length === 0 && !loading && (
                  <tr><td colSpan={2} className="px-4 py-8 text-center text-[#737373]">No active balances yet.</td></tr>
                )}
                {(stats?.top_balances || []).map((u) => (
                  <tr key={u.email} className="border-t border-white/5">
                    <td className="px-4 py-2 text-white font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-2 text-right text-[#D4AF37] font-['Bebas_Neue'] text-lg">{u.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Spend breakdown */}
        <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5">
          <div className="px-4 py-3 bg-[#0D0D0D] border-b border-white/5">
            <h3 className="text-sm uppercase tracking-wider text-[#A3A3A3]">Spend Breakdown</h3>
          </div>
          <div className="p-4 space-y-3">
            {(stats?.spend_breakdown || []).length === 0 && !loading && (
              <p className="text-center text-[#737373] py-6">No streams have used credits yet.</p>
            )}
            {(stats?.spend_breakdown || []).map((s) => (
              <div key={s._id} className="flex items-center justify-between bg-[#0D0D0D] border border-white/5 px-4 py-3">
                <div>
                  <p className="text-sm text-white">
                    {s._id === 'track_stream' ? 'Track streams' : s._id === 'book_audio_stream' ? 'Audiobook listens' : s._id}
                  </p>
                  <p className="text-xs text-[#737373]">{s.count} plays</p>
                </div>
                <p className="font-['Bebas_Neue'] text-2xl text-[#D4AF37]">{s.credits} cr</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsTab;
